<script setup lang="ts">
import type { NotificationDto } from '@schwrz/shared';
import { onClickOutside } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useAuthStore } from '../../stores/auth';
import { useNotificationsStore } from '../../stores/notifications';

const auth = useAuthStore();
const store = useNotificationsStore();
const router = useRouter();

const open = ref(false);
const root = ref<HTMLElement | null>(null);

/** Ids that were unread when the panel was last opened — shown with a dot until next open. */
const snapshotUnreadIds = ref<Set<string>>(new Set());

let pollTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  if (!auth.isAuthenticated) return;
  void store.refreshUnreadCount();
  pollTimer = setInterval(() => {
    if (auth.isAuthenticated) void store.refreshUnreadCount();
  }, 60_000);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

onClickOutside(root, () => {
  open.value = false;
});

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') open.value = false;
}

async function toggle(): Promise<void> {
  if (!open.value) {
    // Snapshot currently unread ids before they are cleared.
    snapshotUnreadIds.value = new Set(
      store.items.filter((n) => !n.readAt).map((n) => n.id),
    );
    open.value = true;
    await store.loadList();
    // Snapshot again from the freshly-loaded list (covers first open).
    snapshotUnreadIds.value = new Set(
      store.items.filter((n) => !n.readAt).map((n) => n.id),
    );
    // Clear the badge immediately — phone-style.
    if (store.unreadCount > 0) void store.markAllRead();
  } else {
    open.value = false;
  }
}

const badgeLabel = computed(() => {
  if (store.unreadCount <= 0) return '';
  return store.unreadCount > 99 ? '99+' : String(store.unreadCount);
});

const hasReadNotifications = computed(() => store.items.some((n) => n.readAt));
const clearing = ref(false);

async function onClearRead(): Promise<void> {
  if (clearing.value || !hasReadNotifications.value) return;
  clearing.value = true;
  try {
    await store.clearRead();
    snapshotUnreadIds.value = new Set(
      store.items.filter((n) => !n.readAt).map((n) => n.id),
    );
  } finally {
    clearing.value = false;
  }
}

// ---------- Grouping ----------

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

interface DayGroup {
  label: string;
  key: string;
  items: NotificationDto[];
}

const grouped = computed<DayGroup[]>(() => {
  if (!store.items.length) return [];

  const today = dayKey(new Date().toISOString());
  const yesterday = dayKey(new Date(Date.now() - 86_400_000).toISOString());

  const map = new Map<string, NotificationDto[]>();
  for (const n of store.items) {
    const k = dayKey(n.createdAt);
    const arr = map.get(k) ?? [];
    arr.push(n);
    map.set(k, arr);
  }

  return [...map.entries()].map(([key, items]) => {
    let label: string;
    if (key === today) label = 'Сегодня';
    else if (key === yesterday) label = 'Вчера';
    else {
      const d = new Date(key + 'T00:00:00');
      label = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    }
    return { label, key, items };
  });
});

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function isUnread(n: NotificationDto): boolean {
  return snapshotUnreadIds.value.has(n.id);
}

function onRowClick(n: NotificationDto): void {
  if (!n.url) return;
  open.value = false;
  void router.push(n.url);
}
</script>

<template>
  <div
    ref="root"
    class="notif-bell"
    @keydown="onKeydown"
  >
    <button
      type="button"
      class="btn btn--ghost btn--small header-search-btn header-bell-btn"
      aria-label="Уведомления"
      title="Уведомления"
      :aria-expanded="open"
      @click="toggle"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <span
        v-if="badgeLabel"
        class="notif-bell__badge"
        aria-hidden="true"
      >{{ badgeLabel }}</span>
    </button>

    <div
      v-show="open"
      class="notif-panel"
      role="dialog"
      aria-label="Уведомления"
    >
      <div class="notif-panel__head">
        <span class="notif-panel__heading">Уведомления</span>
        <button
          v-if="hasReadNotifications"
          type="button"
          class="btn btn--ghost btn--small notif-panel__clear"
          :disabled="clearing"
          title="Удалить просмотренные уведомления"
          @click="onClearRead"
        >
          Очистить
        </button>
      </div>

      <div class="notif-panel__body">
        <template v-if="store.loading && !store.items.length">
          <p class="notif-panel__empty">
            Загрузка…
          </p>
        </template>
        <template v-else-if="!grouped.length">
          <p class="notif-panel__empty">
            Нет уведомлений.
          </p>
        </template>
        <template v-else>
          <div
            v-for="group in grouped"
            :key="group.key"
            class="notif-panel__group"
          >
            <p class="notif-panel__group-label">
              {{ group.label }}
            </p>
            <ul class="notif-panel__list">
              <li
                v-for="n in group.items"
                :key="n.id"
                :class="['notif-panel__row', { 'is-unread': isUnread(n), 'is-clickable': !!n.url }]"
                @click="onRowClick(n)"
              >
                <span
                  v-if="isUnread(n)"
                  class="notif-panel__unread-dot"
                  aria-hidden="true"
                ></span>
                <div class="notif-panel__row-content">
                  <p class="notif-panel__row-title">
                    {{ n.title }}
                  </p>
                  <!-- eslint-disable vue/no-v-html -- server wraps due time in <strong> only -->
                  <p
                    class="notif-panel__row-body"
                    v-html="n.body"
                  ></p>
                  <!-- eslint-enable vue/no-v-html -->
                </div>
                <span class="notif-panel__row-time">{{ timeLabel(n.createdAt) }}</span>
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
